import { PhpArray, PhpFloat, type PhpKey, phpCopy } from '../../php-compat';
import type { PortEnv } from '../../port-contract';

/** Storage arrays, including ordered tagged arrays from the referee. Page
 * readers stay dependencies: this accessor does not interpret compiled fields. */
export type Storage = Record<string, unknown> | unknown[] | PhpArray;
export function isArray(value: unknown): value is Storage {
    return value instanceof PhpArray || value !== null && typeof value === 'object' && !(value instanceof PhpFloat);
}
export function get(value: unknown, ...path: (string | number)[]): unknown {
    for (const key of path) {
        if (!isArray(value)) return undefined;
        value = value instanceof PhpArray ? value.get(key) : (value as Record<string, unknown>)[key];
    }
    return value;
}
export function entries(value: unknown): [PhpKey, unknown][] {
    if (value instanceof PhpArray) return [...value];
    if (!isArray(value)) return [];
    return [...new PhpArray(Object.entries(value))];
}
export function values(value: unknown): unknown[] { return entries(value).map(([, v]) => v); }
export function array(...pairs: [PhpKey, unknown][]): PhpArray { return new PhpArray(pairs); }
export function copy<T>(value: T): T { return phpCopy(value); }
export function set(row: Storage, key: string, value: unknown): null {
    if (row instanceof PhpArray) row.set(key, value);
    else (row as Record<string, unknown>)[key] = value;
return null;
}

/** Each callback is the actual PHP reader's calling convention. Their oracle
 * wrappers record real calls and replay the results; no port guesses a lean field. */
export interface Readers {
    item_field(row: unknown, key: string): unknown;
    layer_value(row: unknown, key: string): unknown;
    layer_entries(row: unknown, key: string): Storage;
    campaign_field(row: unknown, key: string): unknown;
    encounter_field(row: unknown, key: string): unknown;
    encounter_creature_field(row: unknown, key: string): unknown;
    inventory_field(row: unknown, key: string): unknown;
    creature_statuses(row: unknown, mode: string): Storage;
    computed_value(row: unknown, bucket: string, key: string): unknown;
}
export type ReadEnv<D extends object = object> = PortEnv<Readers & D>;

/** HasIdByVariable's catalog memo is observable PHP static state, so it belongs
 * in PortEnv, even though the catalog itself is immutable. */
export function catalogId(env: Pick<PortEnv<object>, 'globals'>, className: string, variable: string, id: string | null): string | null {
    const key = className + '::$idCache', prior = env.globals[key];
    const cached = get(prior, variable);
    if (cached != null) return cached as string;
    const next = prior instanceof PhpArray ? prior.copy() : new PhpArray(entries(prior));
    next.set(variable, id); env.globals[key] = next;
    return id;
}
